import React, { useEffect, useState } from 'react';
import {
  alpha,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Link,
  Stack,
  Typography,
  Alert,
  LinearProgress,
  Grid,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AssessmentIcon from '@mui/icons-material/Assessment';
import GavelIcon from '@mui/icons-material/Gavel';
import DescriptionIcon from '@mui/icons-material/Description';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import { useNavigate, useLocation } from 'react-router';
import { RouterLinks } from '../../../app-route-paths';
import { ComplianceService } from '@zeta-services';
import { WaitScreenCompliance } from './wait-screen';

// ------------------------------------------------
// Types
// ------------------------------------------------
type FindingStatus = 'Pass' | 'Fail' | 'Partial' | 'Not Evidenced';

type Finding = {
  section: string;
  status: FindingStatus;
  compliance_percentage: string;
  fca_reference: string[];
  issues: string[];
  feedback: {
    client: string;
    advisor: string;
    examples: { quote: string; location_hint?: string }[];
  };
  remediation: string[];
};

type ComplianceReviewResult = {
  document_type: string;
  recognised: boolean;
  summary: string;
  total_compliance_percentage: string;
  findings: Finding[];
  validation_scope: {
    external_sources_used: boolean;
    prior_knowledge_used: boolean;
    full_document_scanned: boolean;
    assessment_basis: string;
  };
};

// ------------------------------------------------
// Helpers
// ------------------------------------------------
const percent = (p: string) => Number(p.replace('%', '')) || 0;

const statusFromPercent = (p: number) => {
  if (p >= 100) return { label: 'Pass', color: 'success' as const, icon: <CheckCircleIcon /> };
  if (p >= 50) return { label: 'Partial', color: 'warning' as const, icon: <WarningAmberIcon /> };
  return { label: 'Review', color: 'error' as const, icon: <GavelIcon /> };
};

// ------------------------------------------------
// Screen
// ------------------------------------------------
export default function ComplianceReviewResultsScreen() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { state } = useLocation();
  const reviewId = state?.reviewId;

  const [data, setData] = useState<ComplianceReviewResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);

    ComplianceService()
      .fetchReview({ reviewId })
      .then((res: any) => {
        const parsed =
          typeof res.data.result === 'string'
            ? JSON.parse(res.data.result)
            : res.data.result;

        setData(parsed);
        setError(null);
      })
      .catch(() => setError('Unable to load compliance review.'))
      .finally(() => setLoading(false));
  }, [reviewId]);

  const overallScore = percent(data?.total_compliance_percentage || '0%');
  const overallStatus = statusFromPercent(overallScore);

  // ------------------------------------------------
  // Render
  // ------------------------------------------------
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link underline="hover" sx={{ cursor: 'pointer' }} onClick={() => navigate(RouterLinks.complianceReviews)}>
            Compliance Reviews
          </Link>
          <Typography color="text.primary">Review Results</Typography>
        </Breadcrumbs>

        {!loading && error && <Alert severity="error">{error}</Alert>}

        {!loading && data && (
          <>
            {/* ================= OVERALL SUMMARY ================= */}
            <Card
              sx={{
                borderRadius: 4,
                mb: 4,
                position: 'relative',
                overflow: 'hidden',
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 6, bgcolor: 'primary.main' }} />
              <CardContent sx={{ pl: 4 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={4}>
                  <Box flex={1}>
                    <Typography variant="overline" color="text.secondary" sx={{ p: 2 }}>
                      Regulatory assessment
                    </Typography>
                    <Typography variant="h5" fontWeight={600} sx={{ p: 1 }}>
                      Regulatory Review Summary
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                      {data.summary}
                    </Typography>

                    <Stack direction="row" spacing={1} flexWrap="wrap" mt={1}>
                      <Chip icon={<DescriptionIcon />} label={data.document_type} size="small" />
                      <Chip
                        size="small"
                        label={data.recognised ? 'Document recognised' : 'Not recognised'}
                        color={data.recognised ? 'success' : 'default'}
                      />
                      <Chip
                        size="small"
                        variant="outlined"
                        label={data.validation_scope.full_document_scanned ? 'Full document scanned' : 'Partial scan'}
                      />
                    </Stack>
                  </Box>

                  <Box
                    sx={{
                      minWidth: 220,
                      bgcolor: alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.08 : 0.04),
                      borderRadius: 3,
                      px: 3,
                      py: 2.5,
                      textAlign: 'center',
                    }}
                  >
                    <AssessmentIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                    <Typography variant="caption" color="text.secondary" sx={{ p: 2 }}>
                      Overall compliance
                    </Typography>
                    <Typography variant="h3" fontWeight={700} sx={{ p: 2 }}>
                      {data.total_compliance_percentage}
                    </Typography>
                    <Chip icon={overallStatus.icon} label={overallStatus.label} color={overallStatus.color} />
                  </Box>
                </Stack>
              </CardContent>
            </Card>

            {/* ================= FINDINGS (ACCORDIONS) ================= */}
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Detailed obligation findings
            </Typography>

            <Stack spacing={2}>
              {data.findings.map((f, idx) => {
                const score = percent(f.compliance_percentage);
                const status = statusFromPercent(score);

                return (
                  <Accordion
                    key={idx}
                    defaultExpanded={false}
                    sx={{
                      borderRadius: 2,
                      bgcolor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                      '&:before': { display: 'none' },
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Stack direction="row" spacing={2} alignItems="center" width="100%">
                        <Typography fontWeight={600} flex={1}>
                          {f.section}
                        </Typography>
                        <Chip icon={status.icon} label={status.label} color={status.color} size="small" />
                        <Typography fontWeight={600}>{f.compliance_percentage}</Typography>
                      </Stack>
                    </AccordionSummary>

                    <AccordionDetails>
                      <Stack spacing={2}>
                        <LinearProgress variant="determinate" value={score} sx={{ height: 8, borderRadius: 4 }} />

                        {f.issues.length > 0 && (
                          <>
                            <Typography variant="subtitle2">Key issues identified</Typography>
                            <ul>
                              {f.issues.map((i, k) => (
                                <li key={k}>
                                  <Typography variant="body2">{i}</Typography>
                                </li>
                              ))}
                            </ul>
                          </>
                        )}

                        {f.fca_reference.length > 0 && (
                          <>
                            <Typography variant="subtitle2">FCA reference</Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                              {f.fca_reference.map((r, k) => (
                                <Chip key={k} size="small" variant="outlined" label={r} />
                              ))}
                            </Stack>
                          </>
                        )}

                        <Divider />

                        <Grid container spacing={3}>
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2">Client impact</Typography>
                            <Typography variant="body2">{f.feedback.client}</Typography>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2">Advisor guidance</Typography>
                            <Typography variant="body2">{f.feedback.advisor}</Typography>
                          </Grid>
                        </Grid>

                        {f.feedback.examples.length > 0 && (
                          <>
                            <Divider />
                            <Typography variant="subtitle2">Evidence from document</Typography>
                            <Stack spacing={1}>
                              {f.feedback.examples.map((e, k) => (
                                <Paper
                                  key={k}
                                  variant="outlined"
                                  sx={{
                                    p: 2,
                                    bgcolor: alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.08 : 0.04),
                                    borderColor: 'divider',
                                  }}
                                >
                                  <Typography variant="body2" fontStyle="italic">
                                    “{e.quote}”
                                  </Typography>
                                  {e.location_hint && (
                                    <Typography variant="caption" color="text.secondary">
                                      {e.location_hint}
                                    </Typography>
                                  )}
                                </Paper>
                              ))}
                            </Stack>
                          </>
                        )}

                        {f.remediation.length > 0 && (
                          <>
                            <Divider />
                            <Typography variant="subtitle2">Recommended remediation actions</Typography>
                            <ul>
                              {f.remediation.map((r, k) => (
                                <li key={k}>
                                  <Typography variant="body2">{r}</Typography>
                                </li>
                              ))}
                            </ul>
                          </>
                        )}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Stack>

            <Box sx={{ mt: 4 }}>
              <Button variant="outlined" onClick={() => navigate(RouterLinks.complianceReviews)}>
                Return to reviews
              </Button>
            </Box>
          </>
        )}

        <WaitScreenCompliance open={loading} title="Assessing suitability and regulatory disclosures…" />
      </Container>
    </Box>
  );
}
